import React, { createContext, useState, useContext, useEffect } from "react";
import { Auth } from "aws-amplify";
import { Button, Modal } from "react-bootstrap";

import PropTypes from "prop-types";
import Login from "../components/Login";
import { logoutUrl } from "../util/Amplify";

const { REACT_APP_API_URL, REACT_APP_API_ENVIRONMENT } = window;

const AuthContext = createContext();

export default function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [version, setVersion] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);
  const [userGroups, setUserGroups] = useState([]);

  const getSession = async () => Auth.currentSession();
  const userGroupsFromSession = (session = {}) => {
    const { idToken: { payload = {} } = {} } = session;
    let groups = [];
    if(payload['custom:group']) {
      try {
        groups = payload['custom:group']
          .replace(/[[\]\s]+/g, '')
          .split(',') || [];
      } catch (error) {
        groups = [];
      }
    }
    return groups;
  }; 

  useEffect(() => {
    const getSessionAsync = async () => {
      try {
        const userSession = await getSession();
        setUserGroups(userGroupsFromSession(userSession));
        setUser(userSession);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    getSessionAsync();
  }, []);

  useEffect(() => {
    const getVersionAsync = async () => {
      try {
        setVersion("v1.2.4"); // TODO: get from package.json
      } catch (error) {
        setVersion("No version available");
      }
    };
    if (process.env.REACT_APP_CONFIG !== "local.config.js") getVersionAsync();
  }, []);

  const login = () => {
    Auth.federatedSignIn();
  };

  const logout = async () => {
    await Auth.signOut();
    window.location = logoutUrl;
  };

  const getAccessToken = async () => {
    try {
      const session = await getSession();
      return session.getAccessToken().getJwtToken();
    } catch (error) {
      logout();
      return null;
    }
  };

  const getUser = async () => {
    try {
      const session = await getSession();
      return session.accessToken.payload.username;
    } catch (error) {
      return null;
    }
  };

  const onError = (error) => Promise.reject(error);

  const onSuccess = async (response) => {
    switch (response.status) {
      case 200: {
        try {
          return await response.json();
        } catch (error) {
          return null;
        }
      }
      case 401:
      case 403:
        setSessionExpired(true);
        return null;
      case 409:
        return onError(new Error('Conflict with existing.'))
      default:
        return onError();
    }
  };

  const buildUrl = (url) =>
    `${REACT_APP_API_URL}/${REACT_APP_API_ENVIRONMENT}/${url}`;

  const get = async (url) => {
    try {
      const IDToken = await getAccessToken();
      const CognitoUser = await getUser();
      const fullUrl = buildUrl(url);

      const res = await fetch(fullUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          CognitoUser,
          IDToken,
        },
      });
      return onSuccess(res);
    } catch (error) {
      return onError(error);
    }
  };

  const post = async (url, data) => {
    try {
      const IDToken = await getAccessToken();
      const CognitoUser = await getUser();
      const fullUrl = buildUrl(url);

      const res = await fetch(fullUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          CognitoUser,
          IDToken,
        },
        body: JSON.stringify(data),
      });
      return onSuccess(res);
    } catch (error) {
      return onError(error);
    }
  };

  const put = async (url, data) => {
    try {
      const IDToken = await getAccessToken();
      const CognitoUser = await getUser();
      const fullUrl = buildUrl(url);

      const res = await fetch(fullUrl, {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          CognitoUser,
          IDToken,
        },
        body: JSON.stringify(data),
      });
      return onSuccess(res);
    } catch (error) {
      return onError(error);
    }
  };

  const deleteResource = async (url, data) => {
    try {
      const IDToken = await getAccessToken();
      const CognitoUser = await getUser();
      const fullUrl = buildUrl(url);

      const res = await fetch(fullUrl, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          CognitoUser,
          IDToken,
        },
        body: JSON.stringify(data),
      });
      return onSuccess(res);
    } catch (error) {
      return onError(error);
    }
  };

  const patch = async (url, data) => {
    try {
      const IDToken = await getAccessToken();
      const CognitoUser = await getUser();
      const fullUrl = buildUrl(url);

      const res = await fetch(fullUrl, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          CognitoUser,
          IDToken,
        },
        body: JSON.stringify(data),
      });
      return onSuccess(res);
    } catch (error) {
      return onError(error);
    }
  };

  if (loading) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{ logout, user, version, get, post, put, patch, userGroups, deleteResource }}
    >
      {user ? children : <Login login={login} />}
      <Modal show={sessionExpired}>
        <Modal.Header>
          <Modal.Title>Session expired</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Your session has expired. Please click logout below to allow you to
          log back in and try again.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={logout}>
            Logout
          </Button>
        </Modal.Footer>
      </Modal>
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
